
import { PropType, computed, defineComponent, onMounted, ref, watch } from 'vue';
import { Instance } from '@/catalogs/api/Instance';
import {
  AddNotificationCommand,
  CatalogGetOptionsCommand,
} from '@/catalogs/api/runtime/CommandExecutor';
import { TranslateFunc } from '@/catalogs/composables/setupComponent';
import PIS from '@/common/api/PIS';
import { NotificationType } from '@/common/api/runtime/INotification';
import {
  CatalogOption,
  CatalogOptionItem,
  CatalogOptionsResult,
  CatalogTypeEnum,
} from '@/common/services/swagger/index.defs';

export default defineComponent({
  props: {
    t: { type: Function as PropType<TranslateFunc>, required: true },
    instanceId: String,
    editable: { type: Boolean, default: true },
    type: { type: String as PropType<CatalogTypeEnum>, default: CatalogTypeEnum.Personal },
    group: { type: String, default: null },
  },
  emits: ['on-type-updated', 'on-customer-group-updated'],
  setup(props, { emit }) {
    const instance = PIS.Catalogs.getInstance(props.instanceId) as Instance;
    const catalogType: typeof CatalogTypeEnum = CatalogTypeEnum;
    const catalogOptions = ref<CatalogOptionsResult['items']>([]);
    const ownerType = ref<CatalogTypeEnum>(props.type);
    const customerGroup = ref<string | null>(props.group);
    const customerGroupSelectDisabled = ref<boolean>(true);
    const computedCatalogOptions = computed<CatalogOption[]>(() => {
      const options = catalogOptions.value || [];
      if (props.type === CatalogTypeEnum.CustomerGroup && props.group) {
        const hasCustomerGroupOption = !!options.find(
          (item: CatalogOption) => item.type === CatalogTypeEnum.CustomerGroup,
        );
        if (!hasCustomerGroupOption) {
          return options.concat([
            {
              type: CatalogTypeEnum.CustomerGroup,
              items: [{ text: props.group, value: props.group }],
            },
          ]);
        }
      }
      return options;
    });
    const customerGroupOptionItems = computed(() => {
      let customerGroupItem: CatalogOption | undefined = computedCatalogOptions.value.find(
        (item: CatalogOption) => item.type === CatalogTypeEnum.CustomerGroup,
      );
      if (!customerGroupItem) {
        return [];
      }
      const items: CatalogOptionItem[] = customerGroupItem.items || [];
      if (props.editable) {
        return items.filter((item: CatalogOptionItem) => item.editable);
      }
      return items;
    });
    watch(
      () => customerGroup.value,
      () => emit('on-customer-group-updated', customerGroup.value),
    );
    watch(
      () => ownerType.value,
      () => {
        if (ownerType.value !== CatalogTypeEnum.CustomerGroup) {
          customerGroup.value = null;
          customerGroupSelectDisabled.value = true;
        } else {
          customerGroupSelectDisabled.value = false;
        }
        emit('on-type-updated', ownerType.value);
      },
    );
    onMounted(async () => {
      try {
        const catalogOptionsResult: CatalogOptionsResult | undefined = (await instance.execute(
          new CatalogGetOptionsCommand(),
        )) as CatalogOptionsResult;
        if (!catalogOptionsResult) {
          throw new Error(props.t('CatalogOptionsLoadingError'));
        }
        catalogOptions.value = catalogOptionsResult.items;
      } catch (error) {
        await instance.execute(
          new AddNotificationCommand({ type: NotificationType.danger, message: error.message }),
        );
      }
    });

    return {
      ownerType,
      customerGroup,
      catalogType,
      computedCatalogOptions,
      customerGroupOptionItems,
      customerGroupSelectDisabled,
    };
  },
});
